<script setup lang="ts">
import {
  createMateSprite,
  deleteMateSprite,
  readMateSprite,
  updateMateSprite,
} from "@/apis/script-game";
import { Direction, Sex } from "@/enums/characterGeneratorEnums";
import api from "@/modules/api";
import { Media } from "@/types/entity";
import { MateSprite, MateSpriteMode } from "@/types/game";
import { SelectedItems } from "@/types/sheet-definiation";
import { showConfirmDialog, showDialog, showLoadingToast } from "vant";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { CharacterGeneratorInstance } from "./CharacterGenerator.vue";
import { useSpriteSheetExport } from "@/use/useSpriteSheetExport";
import { useDungeon } from "@/stores/dungeon";

// Props & Emits
const props = defineProps<{
  show: boolean;
  mateId?: number;
  sprite?: MateSprite;
  hideDelete?: boolean;
  from?: string;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (e: "saved"): void;
  (e: "single-frame"): void;
}>();

// States
const { t } = useI18n();
const dungeonStore = useDungeon();
const name = ref(t("characterGenerator.messages.unnamed"));
const selectedSex = ref<Sex>(Sex.Male);
const selectedItems = ref<SelectedItems>({} as SelectedItems);

const defList = computed(() => {
  return Object.values(selectedItems.value).flatMap((item) =>
    Object.values(item)
  );
});

const { mergeAndUploadSpritesheet } = useSpriteSheetExport();

const loadSprite = () => {
  if (!props.sprite) {
    throw new Error("Sprite not found");
  }
  name.value = props.sprite.name!;
  const prompt: {
    sex: Sex;
    items: SelectedItems;
  } = JSON.parse(props.sprite.prompt!);
  selectedSex.value = prompt.sex;
  selectedItems.value = prompt.items;
};

watch(
  () => props.show,
  (show) => {
    if (!show) return;
    name.value = props.sprite?.name || t("characterGenerator.messages.unnamed");
    if (props.sprite?.prompt) {
      const prompt: {
        sex: Sex;
        items: SelectedItems;
      } = JSON.parse(props.sprite.prompt);
      selectedSex.value = prompt.sex;
      selectedItems.value = prompt.items;
    } else {
      selectedSex.value = Sex.Male;
      selectedItems.value = {} as SelectedItems;
    }
  }
);

// Methods
const save = async () => {
  if (!name.value) {
    await showDialog({
      message: t("characterGenerator.messages.nameRequired"),
      confirmButtonText: t("Buttons.confirm"),
    });
    return;
  }

  const loading = showLoadingToast({
    message: t("Message.saving"),
  });

  try {
    // 1. 导出Spritesheet 和 预览用Gif
    console.log(selectedItems.value);
    let hasCloth = false;
    if (selectedItems.value.Torso) {
      Object.values(selectedItems.value.Torso).forEach((v) => {
        if (Object.keys(v).length > 0) {
          hasCloth = true;
        }
      });
    }
    if (!hasCloth)
      return showDialog({
        message: t(`sprite.needCloth`),
        confirmButtonText: t("Buttons.confirm"),
      });

    const json = JSON.stringify({
      sex: selectedSex.value,
      items: selectedItems.value,
    });
    // if (props.notAutoSave) {
    //   emit("saved", {
    //     name: name.value,
    //     prompt: json,
    //     mode: MateSpriteMode.Compose,
    //     // imageMedia: media,
    //     // frameMedia: spriteMedia,
    //   });
    // } else {

    const [spriteMedia, media] = await Promise.all([
      mergeAndUploadSpritesheet(defList.value, selectedSex.value),
      exportGif(),
    ]);
    if (props.sprite) {
      await updateMateSprite(props.sprite.id!, {
        name: name.value,
        prompt: json,
        mode: MateSpriteMode.Compose,
        imageMediaId: media.id,
        frameMediaId: spriteMedia?.id,
      });
    } else {
      await createMateSprite({
        name: name.value,
        mateId: props.mateId!,
        prompt: json,
        mode: MateSpriteMode.Compose,
        imageMediaId: media.id,
        frameMediaId: spriteMedia?.id,
      });
    }
    await dungeonStore.fetchMateSpriteList(props.mateId!, true);
    await checkSpriteStatusIfIsPending();
    // }
    emit("update:show", false);
    emit("saved");
  } finally {
    loading.close();
  }
};

const checkSpriteStatusIfIsPending = async () => {
  if (!props.sprite?.uuid) return;
  const res = await readMateSprite(props.sprite.uuid!);
  // props.sprite = res.item;
  if (res.item.status === 1) {
    await new Promise((r) => setTimeout(r, 3000));
    checkSpriteStatusIfIsPending();
  } else {
    await dungeonStore.fetchMateSpriteList(props.sprite.mateId!, true);
  }
};

const regenerateSpritesheet = async () => {
  if (!props.sprite) {
    throw new Error("Sprite not found");
  }
  loadSprite();
  const spriteMedia = await mergeAndUploadSpritesheet(
    defList.value,
    selectedSex.value
  );
  await updateMateSprite(props.sprite.id!, {
    frameMediaId: spriteMedia!.id,
  });
};

export interface CharacterGeneratorPopupInstance {
  regenerateSpritesheet: () => Promise<void>;
  exportGif: () => Promise<Media>;
}

const setDefault = async () => {
  await updateMateSprite(props.sprite!.id!, {
    isDefault: true,
  });
  props.sprite!.isDefault = true;
  emit("saved");
};

const deleteSprite = async () => {
  showConfirmDialog({
    title: t("characterGenerator.messages.sureDeleteSprite"),
    confirmButtonText: t("Buttons.delete"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      await deleteMateSprite(props.sprite!.id!);
      emit("update:show", false);
      emit("saved");
    })
    .catch(() => {
      // ignore
    });
};

const characterGeneratorRef = ref<CharacterGeneratorInstance | null>(null);

const exportGif = async (): Promise<Media> => {
  if (!characterGeneratorRef.value) {
    throw new Error("Character generator not initialized");
  }

  return new Promise((resolve) => {
    characterGeneratorRef.value!.exportCharacterGif(Direction.Down, {
      onComplete: (blob) => {
        api.uploadMedia(0, new File([blob], "sprite.gif")).then((res) => {
          resolve(res.item);
        });
      },
    });
  });
};

defineExpose({
  regenerateSpritesheet,
  exportGif,
});
const showPolicy = ref(false);
</script>

<template>
  <VanPopup
    position="bottom"
    :show="show"
    @update:show="emit('update:show', $event)"
    class="h-full grid grid-rows-[auto_1fr] font-[Cubic]"
    :close-on-click-overlay="false"
  >
    <ThreadsNav show-back :go-back="() => emit('update:show', false)">
      <template #left>
        <template v-if="sprite && from !== 'mate-create'">
          <button
            v-if="!sprite?.isDefault"
            class="btn btn-xs rounded btn-outline"
            @click="setDefault"
          >
            {{ $t("Buttons.setDefault") }}
          </button>
          <div
            v-else
            class="flex items-center gap-1 text-xs text-base-content/50"
          >
            <i class="iconify mingcute--check-circle-line" />
            {{ $t("Message.default") }}
          </div>
        </template>
      </template>
      <template #center>
        <button
          class="btn btn-xs btn-link text-base-content/50 p-0"
          @click="showPolicy = true"
        >
          {{ $t("characterGenerator.messages.copyright") }}
        </button>
      </template>
      <template #right>
        <button
          v-if="sprite && !hideDelete && from !== 'mate-create'"
          class="btn btn-xs rounded btn-outline"
          @click="deleteSprite"
        >
          {{ $t("Buttons.delete") }}
        </button>
        <button
          v-if="sprite?.animationzz?.length && sprite.animationzz[0].mediaId"
          class="btn btn-xs rounded"
          @click="emit('single-frame')"
        >
          单帧编辑
        </button>
        <button class="btn btn-xs rounded btn-primary" @click="save">
          {{ $t("Buttons.save") }}
        </button>
      </template>
    </ThreadsNav>

    <CharacterGenerator
      ref="characterGeneratorRef"
      v-model:selected-sex="selectedSex"
      v-model:selected-items="selectedItems"
      v-model:name="name"
      :show-name-edit="from !== 'mate-create'"
    />
  </VanPopup>
  <ModalPolicyPage v-model:show="showPolicy" :docType="'lpc'" />
</template>
